import { Typography, Box } from '@mui/material';
import { useMemo, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributeLine } from './styles';
import ExpandableImage from '../../Shared/ExpandableImage';
import { theme } from '../../../../../legacy/style/theme';
import Measure from '../../Shared/Measure';
import LandmarkValue from '../../../../../components/streetView/roadDatasPanel/values/LandmarkValue';
import { useAppSelector } from '../../../../../../types';
import { sessionByIdQuery } from '../../../../../../core/sessions/queries';
import { COLOR_BY_RUBRIC } from '../../../network-map-road-data-layer-configuration';
import { WidthProperties } from '../../../../../../core/domain/map';

type Props = {
    properties: WidthProperties;
};

const WidthAttributes: FC<Props> = ({ properties }) => {
    const { t } = useTranslation();

    return <AttributeLine label={t('map.popup.measured_width')} value={`${properties?.measuredWidth}m`} />;
};

const WidthAndSurfaceAttributes: FC<Props> = ({ properties }) => {
    const { t } = useTranslation();

    return (
        <>
            <AttributeLine label={t('map.popup.measured_width')} value={`${properties?.measuredWidth}m`} />
            <AttributeLine label={t('map.popup.section_surface')} value={`${properties?.sectionSurface}m²`} />
            <AttributeLine label={t('map.popup.total_surface')} value={`${properties?.totalSurface}m²`} />
        </>
    );
};

const getAttributesByRubricCode = (rubricCode: string): FC<Props> | null => {
    switch (rubricCode) {
        case 'LARGEUR':
            return WidthAttributes;
        case 'LARGEUR_ET_SURFACE':
        case 'LARGEUR_ET_SURFACE_II':
        case 'LARGEUR_ET_SURFACE_III':
            return WidthAndSurfaceAttributes;
        default:
            return null;
    }
};

const Content: FC<Props> = ({ properties }) => {
    const { t } = useTranslation();
    const session = useAppSelector(sessionByIdQuery(properties.sessionId));

    const AttributesComponent = getAttributesByRubricCode(properties.rubricCode);

    const landmark = useMemo(
        () => ({
            label: properties.landmarkStartLabel,
            sign: properties.landmarkStartSign,
            abd: properties.landmarkStartAbd,
        }),
        [properties.landmarkStartLabel, properties.landmarkStartSign, properties.landmarkStartAbd],
    );

    return (
        <Box marginBottom=".5rem">
            <Typography
                noWrap
                sx={{
                    wordBreak: 'break-all',
                    fontWeight: 600,
                    color: theme.color.black.main,
                }}
            >
                {session?.name}
            </Typography>
            <Measure label={t('map.popup.cumul-start')} value={<LandmarkValue landmark={landmark} unit="m" />} />
            <ExpandableImage sessionId={properties.sessionId} imageIndex={properties.imageIndex} />
            <Box
                boxSizing="border-box"
                marginTop="-6px"
                padding="10px"
                color="white"
                width="100%"
                borderRadius="0 0 5px 5px"
                bgcolor={COLOR_BY_RUBRIC[properties.rubricCode]}
            >
                {AttributesComponent && <AttributesComponent properties={properties} />}
            </Box>
        </Box>
    );
};

export default Content;
